import {
  MedicationRequestDosageInstructionAdministrationCode,
  MedicationType,
} from '@quromedical/fhir-common';
import { z } from 'zod';
import {
  DisplayReferenceSchema,
  UUIDSchema,
  UUIDSearchSchema,
} from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import {
  type PaginatedBundle,
  PaginatedBundleSchema,
  PeriodSchema,
} from '../domain/schema';

/**
 * Loosely based on FHIR Quantity resource but uses a string instead
 */
export const MedicationMeasureSchema = z.object({
  value: z.string(),
  unit: z.string(),
});

export const MedicationScheduleSchema = z.string().refine(
  (val) => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 1 && num <= 6;
  },
  {
    message: 'String must be a valid number between 1 and 6',
  },
);

export enum MedicationRouteCode {
  TAB = 'TAB',
  SYR = 'SYR',
  CAP = 'CAP',
  LOZ = 'LOZ',
  MOW = 'MOW',
  SPR = 'SPR',
  NAS = 'NAS',
  NOD = 'NOD',
  EAR = 'EAR',
  OIN = 'OIN',
  DRP = 'DRP',
  SOP = 'SOP',
  EMO = 'EMO',
  DSP = 'DSP',
  EFT = 'EFT',
  CRE = 'CRE',
  OPD = 'OPD',
  CHU = 'CHU',
  SHA = 'SHA',
  VAT = 'VAT',
  PAD = 'PAD',
  LOT = 'LOT',
  POW = 'POW',
  SRT = 'SRT',
  VCR = 'VCR',
  GRA = 'GRA',
  SLN = 'SLN',
  INH = 'INH',
  ENE = 'ENE',
  TBL = 'TBL',
  SUP = 'SUP',
  SOL = 'SOL',
  SAC = 'SAC',
  EYO = 'EYO',
  STR = 'STR',
  UNI = 'UNI',
  CPS = 'CPS',
  INF = 'INF',
  POR = 'POR',
  VAG = 'VAG',
  UNG = 'UNG',
  PAI = 'PAI',
  EED = 'EED',
  EEO = 'EEO',
  SCR = 'SCR',
  NAG = 'NAG',
  SRC = 'SRC',
  PAS = 'PAS',
  SUS = 'SUS',
  WAF = 'WAF',
  SWB = 'SWB',
  PES = 'PES',
  GEL = 'GEL',
  DOU = 'DOU',
  UDV = 'UDV',
  IMP = 'IMP',
  ATH = 'ATH',
  MLS = 'MLS',
  VAC = 'VAC',
  KIT = 'KIT',
  AMP = 'AMP',
  PAT = 'PAT',
  BDL = 'BDL',
  TUL = 'TUL',
  PLA = 'PLA',
  BAN = 'BAN',
  GAU = 'GAU',
  IUD = 'IUD',
  NEB = 'NEB',
  VAF = 'VAF',
  VRI = 'VRI',
  DRO = 'DRO',
  VIA = 'VIA',
  MET = 'MET',
  INJ = 'INJ',
  TES = 'TES',
  CLS = 'CLS',
  LIQ = 'LIQ',
  SNG = 'SNG',
  COT = 'COT',
  BRA = 'BRA',
  AQS = 'AQS',
  EMU = 'EMU',
  FOA = 'FOA',
  ECT = 'ECT',
  DEV = 'DEV',
  NDL = 'NDL',
  ACC = 'ACC',
  PDR = 'PDR',
  EEN = 'EEN',
  CRY = 'CRY',
  SUT = 'SUT',
  SUN = 'SUN',
  TUH = 'TUH',
  FCT = 'FCT',
  SWS = 'SWS',
  CHG = 'CHG',
  OLT = 'OLT',
  PVC = 'PVC',
  NSS = 'NSS',
  ORS = 'ORS',
  BAS = 'BAS',
  LFI = 'LFI',
  SPO = 'SPO',
  LZS = 'LZS',
  ODT = 'ODT',
  CHC = 'CHC',
  OSP = 'OSP',
  SLT = 'SLT',
  PAP = 'PAP',
  GEO = 'GEO',
  PAO = 'PAO',
  UDL = 'UDL',
  SLW = 'SLW',
  OOL = 'OOL',
  PWO = 'PWO',
  SPC = 'SPC',
  LIT = 'LIT',
  SCT = 'SCT',
  CRO = 'CRO',
  NIC = 'NIC',
  PSD = 'PSD',
  SCL = 'SCL',
  TPN = 'TPN',
  MC2 = 'MC2',
  OPG = 'OPG',
  DRE = 'DRE',
  MC3 = 'MC3',
  OPI = 'OPI',
  GAS = 'GAS',
  MC1 = 'MC1',
  CWG = 'CWG',
  CPD = 'CPD',
  LIG = 'LIG',
  PTD = 'PTD',
  CAT = 'CAT',
  CES = 'CES',
  DES = 'DES',
  POI = 'POI',
  MLT = 'MLT',
  DEB = 'DEB',
  BIS = 'BIS',
  CPC = 'CPC',
  GRE = 'GRE',
  GUS = 'GUS',
  JES = 'JES',
  OIR = 'OIR',
  SLL = 'SLL',
  STI = 'STI',
  STO = 'STO',
  OGS = 'OGS',
  JUT = 'JUT',
  REN = 'REN',
  HEA = 'HEA',
  ODF = 'ODF',
  ZZZ = 'ZZZ',
  PLS = 'PLS',
  CSM = 'CSM',
  NIH = 'NIH',
  FLS = 'FLS',
  PFS = 'PFS',
}

export const MedicationRouteCodeDescription: Record<
  MedicationRouteCode,
  string
> = {
  TAB: 'TABLET',
  SYR: 'SYRUP',
  CAP: 'CAPSULE',
  LOZ: 'LOZENGE',
  MOW: 'MOUTH WASH',
  SPR: 'SPRAY',
  NAS: 'NASAL SPRAY',
  NOD: 'NOSE DROPS',
  EAR: 'EAR DROPS',
  OIN: 'OINTMENT',
  DRP: 'DROPS',
  SOP: 'SOAP',
  EMO: 'EMOLLIENT',
  DSP: 'DISPERSABLE TABLET',
  EFT: 'EFFERVESCENT TABLET',
  CRE: 'CREAM',
  OPD: 'OPHTHALMIC DROPS',
  CHU: 'CHEW TABLET',
  SHA: 'SHAMPOO',
  VAT: 'VAGINAL TABLET',
  PAD: 'ADHESIVE PAD',
  LOT: 'LOTION',
  POW: 'POWDER',
  SRT: 'SLOW RELEASE TABLET',
  VCR: 'VAGINAL CREAM',
  GRA: 'GRANULES',
  SLN: 'SOLUTION UNBREAKABLE',
  INH: 'INHALER',
  ENE: 'ENEMA',
  TBL: 'REAGENT TABLET',
  SUP: 'SUPPOSITORY',
  SOL: 'SOLUTION BREAKABLE',
  SAC: 'SACHET',
  EYO: 'EYE OINTMENT',
  STR: 'STRIPS',
  UNI: 'INHALER DEVICE',
  CPS: 'SPINCAP',
  INF: 'INFUSION',
  POR: 'POWDER EXTERNAL USE',
  VAG: 'VAGINAL PREPARATION',
  UNG: 'UNG',
  PAI: 'PAINT',
  EED: 'EYE AND EAR DROPS',
  EEO: 'EYE/EAR OINTMENT',
  SCR: 'SCRUB',
  NAG: 'NASAL GEL',
  SRC: 'SLOW RELEASE CAP',
  PAS: 'PASTE',
  SUS: 'SUSPENSION',
  WAF: 'WAFER',
  SWB: 'SWAB',
  PES: 'PESSARY',
  GEL: 'GEL',
  DOU: 'DOUCHE',
  UDV: 'UNI DOSE VIAL',
  IMP: 'IMPLANT',
  ATH: 'AUTOHALER',
  MLS: 'LOCAL APPLICATION',
  VAC: 'VACCINE',
  KIT: 'KIT',
  AMP: 'AMPULE',
  PAT: 'PATCH',
  BDL: 'DEBRIDEMENT APPL',
  TUL: 'TULLE',
  PLA: 'PLASTER',
  BAN: 'BANDAGE',
  GAU: 'GAUZE',
  IUD: 'INTRA UTERINE DEVICE',
  NEB: 'NEBULISING SOLUTION',
  VAF: 'VAGINAL FOAM',
  VRI: 'VAGINAL RING',
  DRO: 'REAGENT DROP',
  VIA: 'VIAL',
  MET: 'METER',
  INJ: 'INJECTION',
  TES: 'TEST',
  CLS: 'CLEANSER',
  LIQ: 'LIQUID',
  SNG: 'SYRINGE',
  COT: 'COTTON WOOL',
  BRA: 'KNEE BRACE',
  AQS: 'AQUA SPRAY',
  EMU: 'EMULSION',
  FOA: 'FOAM',
  ECT: 'ENTERIC COATED TAB',
  DEV: 'INJECTION DEVICE',
  NDL: 'NEEDLE',
  ACC: 'ACCUHALER',
  PDR: 'POWDER (GALENICAL)',
  EEN: 'EAR/EYE/NOSE DROPS',
  CRY: 'CRYSTALS',
  SUT: 'SUTURE',
  SUN: 'SINGLE UNIT NAS SPR',
  TUH: 'TURBUHALER',
  FCT: 'FILM COATED TABLET',
  SWS: 'WIPES',
  CHG: 'CHEW GUM',
  OLT: 'OIL',
  PVC: 'VAGINAL CAP',
  NSS: 'NASULE',
  ORS: 'ORAL SOLUTION',
  BAS: 'BANDAGE & SUBSTANCE',
  LFI: 'LIQ FOR INHALATION',
  SPO: 'SPRAYS ORAL TOPICAL',
  LZS: 'LOZENGE SYSTEMIC',
  ODT: 'ORAL DRUG TEAS',
  CHC: 'CHEW CAP',
  OSP: 'SPRAYS ORAL SYSTEMIC',
  SLT: 'SUBLINGUAL TABLET',
  PAP: 'PAPER',
  GEO: 'GELS ORAL TOPICAL',
  PAO: 'PASTES ORAL TOPICAL',
  UDL: 'UNIT DOSE ORAL LIQ',
  SLW: 'SOAP LIQUID WASH',
  OOL: 'OIL ORAL',
  PWO: 'POWDER ORAL TOPICAL',
  SPC: 'SPRINKLE CAPS',
  LIT: 'LIQUID TOPICAL',
  SCT: 'PROFESIONAL SERVICES',
  CRO: 'CREAMS ORAL TOPICAL',
  NIC: 'NASAL INH CAP TOPICA',
  PSD: 'PAD WITH SUBSTANCE',
  SCL: 'SCALP LOTION',
  TPN: 'PARENTERAL NUTRITION',
  MC2: 'MULTI COMP VARIAT 2',
  OPG: 'OPHTHALMIC GEL',
  DRE: 'DRESSING',
  MC3: 'MULTI COMP VARIAT 3',
  OPI: 'INTRA OCULAR INJ',
  GAS: 'GAS FOR INH',
  MC1: 'MULTI COMP VARIAT 1',
  CWG: 'COLLAGEN WOUND GEL',
  CPD: 'COLLAGEN POWDER',
  LIG: 'LIQUID ORAL GEL',
  PTD: 'PATCH TRANSDERMAL',
  CAT: 'CATHETER',
  CES: 'CEMENT W SUBSTANCE',
  DES: 'DRUG ELUTING STENT',
  POI: 'POWDER FOR INJ',
  MLT: 'MELT TABS',
  DEB: 'DRUG ELUTING BALLOON',
  BIS: 'BISCUITS',
  CPC: 'COMBINATION PACK CAP',
  GRE: 'GRANULES EXTERNAL',
  GUS: 'GAUZE WITH SUBSTANCE',
  JES: 'JEL SYSTEMATIC',
  OIR: 'OINTMENT RECTAL SYST',
  SLL: 'SUBLINGUAL LIQUIDS',
  STI: 'TOPICAL STICKS',
  STO: 'STRIPS ORAL',
  OGS: 'OPHTHALMIC GAS',
  JUT: 'JEL TOPICAL U DOSE',
  REN: 'RENTAL',
  HEA: 'HEARING AID',
  ODF: 'ORODISPERSIBLE FILM',
  ZZZ: 'UNKNOWN',
  PLS: 'PLASTER W/SUBSTANCE',
  CSM: 'CONSUMABLE',
  NIH: 'NASAL INHALER',
  FLS: 'FLOS (DRIED PLANT)',
  PFS: 'PRE-FILLED SYRINGE',
};

export const MedicationRouteCodeSchema = z.nativeEnum(MedicationRouteCode);

export const MedicationTimeUnitSchema = z
  .enum(['s', 'min', 'h', 'd', 'wk', 'mo', 'a'])
  .optional();

export const MedicationUsageStatuses = [
  'active',
  'completed',
  'entered-in-error',
  'intended',
  'stopped',
  'on-hold',
  'unknown',
  'not-taken',
] as const;

export const MedicationTypeSchema = z.nativeEnum(MedicationType);

export const MedicationUsageStatusSchema = z.enum(MedicationUsageStatuses);

/**
 * Makes use of a `frequency`, `period` and `periodUnit`
 * to express timings likes: like "3 times per 1 day"
 * as `frequency` times per `period` `periodUnit`
 *
 * where:
 *
 * ```
 * frequency = 3
 * period = 1
 * periodUnit = d
 * ```
 */
export const MedicationRepeatSchema = z.object({
  frequency: z.number(),
  period: z.number(),
  periodUnit: MedicationTimeUnitSchema,
  type: MedicationTypeSchema.optional(),
});

export const MedicationDurationSchema = z.object({
  value: z.number(),
  unit: z.string(),
});

/**
 * A dosage for a `MedicationStatement`. Makes use of a `frequency`, `period` and `periodUnit`
 * to express timings likes: like "3 times per 1 day" as `frequency` times per `period` `periodUnit`
 *
 * where:
 *
 * ```
 * frequency = 3
 * period = 1
 * periodUnit = d
 * ```
 */
export const MedicationDosageSchema = z.object({
  route: MedicationRouteCodeSchema,
  timingRepeat: MedicationRepeatSchema,
  additionalInstruction: z
    .nativeEnum(MedicationRequestDosageInstructionAdministrationCode)
    .optional(),
});

/**
 * Aligned to the Medprax data
 */
export const MedicationSchema = z.object({
  code: z.string(),
  name: z.string(),
  nappiCode: z.string(),
  strength: MedicationMeasureSchema.optional(),
  volume: MedicationMeasureSchema.optional(),
  individualPackSize: MedicationMeasureSchema.optional(),
  dispensingVolumePacksize: z.string().optional(),
  schedule: z.string().optional(),
  route: z.nativeEnum(MedicationRouteCode),
  type: MedicationTypeSchema.optional(),
});

/**
 * Represents a single medication and the details under which a patient is taking it for.
 *
 * This is a model representation of a FHIR MedicationStatement - different to a prescription.
 * Do we have some other business term for this? `PatientMedications`?
 *
 * A patient's medication may change over the course of their care, in doing so we may want to
 * create additional MedicationStatements and mark the current one as complete/cancelled/etc.
 */
export const BaseMedicationReferenceSchema = z.object({
  /** `fhir4.MedicationStatement.subject` or `fhir4.MedicationRequest.subject` */
  patient: DisplayReferenceSchema,
  /** `fhir.MedicationStatement.note.text` or `fhir4.MedicationRequest.note.text` */
  admission: DisplayReferenceSchema,
  /** Practitioner that prescribed the medication.
   * `fhir4.MedicationStatement.informationSource`  or `fhir4.MedicationRequest.recorder` */
  recorder: DisplayReferenceSchema,
  /** `fhir.MedicationStatement.medicationCodeableConcept` */
  medication: MedicationSchema,
  /** `fhir4.MedicationStatement.dosage` */
  dosage: MedicationDosageSchema,
  /** `fhir4.MedicationStatement.dateAsserted` or `fhir4.MedicationRequest.authoredOn`  */
  date: z.string(),
  noteText: z.string().optional(),
  type: MedicationTypeSchema.optional(),
});

export const PartialBaseMedicationReferenceSchema = z.object({
  patient: DisplayReferenceSchema.optional(),
  admission: DisplayReferenceSchema.optional(),
  noteText: z.string().optional(),
  recorder: DisplayReferenceSchema.optional(),
  medication: MedicationSchema.partial(),
  dosage: MedicationDosageSchema.partial(),
  date: z.string().optional(),
  groupIdentifier: z.string().optional(),
  type: MedicationTypeSchema.optional(),
});

export type PartialBaseMedicationReference = z.infer<
  typeof PartialBaseMedicationReferenceSchema
>;

export const MedicationUsageSchema = BaseMedicationReferenceSchema.extend({
  /** `fhir4.MedicationStatement.status` */
  status: MedicationUsageStatusSchema,
  /** `fhir4.MedicationStatement.effectivePeriod` */
  period: PeriodSchema,
  /** `fhir4.MedicationStatement.derivedFrom` */
  derivedFrom: DisplayReferenceSchema.optional(),
  /** `fhir4.MedicationStatement.identifier`  */
  groupIdentifier: z.string().optional(),
});

export const PartialMedicationUsageSchema =
  PartialBaseMedicationReferenceSchema.extend({
    status: MedicationUsageStatusSchema.optional(),
    period: PeriodSchema.optional(),
    derivedFrom: DisplayReferenceSchema.optional(),
  });

export type PartialMedicationUsage = z.infer<
  typeof PartialMedicationUsageSchema
>;

export const PartialMedicationRequestWithIdSchema =
  PartialMedicationUsageSchema.extend({
    id: UUIDSearchSchema,
  });

export const MedicationUseSchema = z.enum([
  'create-patient-prescription',
  'create-usage',
]);
export type MedicationUse = z.infer<typeof MedicationUseSchema>;

export const MedicationGetAllParamsSchema = z.object({
  use: MedicationUseSchema,
  query: z.string().optional(),
});
export type MedicationGetAllParams = z.infer<
  typeof MedicationGetAllParamsSchema
>;

export const CreateMedicationUsageSchema = MedicationRepeatSchema.extend({
  medicationCode: z.string(),
  noteText: z.string().optional(),
});
export type CreateMedicationUsage = z.infer<typeof CreateMedicationUsageSchema>;

export const MedicationCreateRequestSchema = z.object({
  medicationUsage: z.array(CreateMedicationUsageSchema).min(1),
  patientId: UUIDSchema,
  admissionId: UUIDSchema,
});
export type MedicationCreateRequest = z.infer<
  typeof MedicationCreateRequestSchema
>;

export type UpdateMedicationRequest = {
  status: fhir4.MedicationStatement['status'];
};

export const MedicationGetResponseSchema = PartialMedicationUsageSchema.extend({
  id: UUIDSchema,
});
export type MedicationGetResponse = z.infer<typeof MedicationGetResponseSchema>;

export interface MedicationGetAllResponse extends PaginatedBundle {
  medicationUsages: MedicationGetResponse[];
}
export const MedicationGetAllResponseSchema = PaginatedBundleSchema.extend({
  medicationUsages: z.array(MedicationGetResponseSchema),
});

export const MedicationCreateResponseSchema = z.array(
  MedicationGetResponseSchema,
);

export const MedicationQueryParamsSchema = PaginationSchema.extend({
  subject: UUIDSchema,
  status: MedicationUsageStatusSchema.optional(),
});

export type MedicationQueryParams = z.infer<typeof MedicationQueryParamsSchema>;

export type BaseMedicationReference = z.infer<
  typeof BaseMedicationReferenceSchema
>;
export type MedicationUsage = z.infer<typeof MedicationUsageSchema>;

export type MedicationMeasure = z.infer<typeof MedicationMeasureSchema>;
export type MedicationSchedule = z.infer<typeof MedicationScheduleSchema>;
export type MedicationTimeUnit = z.infer<typeof MedicationTimeUnitSchema>;

export type MedicationDayOfWeek = fhir4.TimingRepeat['dayOfWeek'];

export type MedicationUsageStatus = z.infer<typeof MedicationUsageStatusSchema>;
export type MedicationRepeat = z.infer<typeof MedicationRepeatSchema>;

export type MedicationDosage = z.infer<typeof MedicationDosageSchema>;
export type Medication = z.infer<typeof MedicationSchema>;
export type MedicationDuration = z.infer<typeof MedicationDurationSchema>;

export type MedicationCreateResponse = z.infer<
  typeof MedicationCreateResponseSchema
>;

export type MedicationUpdateResponse = MedicationGetResponse;

export type PatientRecipient = {
  type: 'patient';
};

export type MedicationOrganizationRecipient = {
  type: 'organization';
  id: string;
};

export type MedicationRecipient =
  | PatientRecipient
  | MedicationOrganizationRecipient;
